import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Col, Container, Row, ListGroup, ListGroupItem, Alert, Spinner } from "reactstrap";
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ErrorBoundary } from 'react-error-boundary';
import BreadCrumb from "../../Components/Common/BreadCrumb";
import Widget from "./Widgets";
import BestSellingProducts from "./BestSellingProducts";
import RecentActivity from "./RecentActivity";
import RecentOrders from "./RecentOrders";
import Revenue from "./Revenue";
import SalesByLocations from "./SalesByLocations";
import Section from "./Section";
import StoreVisits from "./StoreVisits";
import TopSellers from "./TopSellers";
import { fetchSales } from "../../utils/dashboard";

const DashboardEcommerce = () => {
  document.title = "Dashboard | SeedNations";
  const navigate = useNavigate();
  const isMounted = useRef(true);

  const [rightColumn, setRightColumn] = useState(false);
  const [dashboardData, setDashboardData] = useState({
    totalSales: {},
    topSalesByLocation: [],
    topCategories: [],
    revenue: {},
    recentOrders: [],
    bestSellingProducts: [],
    bestSellingBrands: []
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const errorNotify = useCallback((error) => {
    if (isMounted.current) {
      toast(error, { 
        position: "top-center", 
        hideProgressBar: true, 
        closeOnClick: false, 
        className: 'bg-danger text-white' 
      });
    }
  }, []);

  const toggleRightColumn = useCallback(() => {
    if (isMounted.current) {
      setRightColumn(prev => !prev);
    }
  }, []);

  const formatDate = useCallback((date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  const loadDashboardData = useCallback(async (dates) => {
    if (!isMounted.current) return;

    try {
      setLoading(true);
      const params = {};
      
      if (!dates) {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(endDate.getDate() - 30);
        params.start_date = formatDate(startDate);
        params.end_date = formatDate(endDate);
      } else {
        params.start_date = formatDate(dates[0]);
        params.end_date = dates[1] ? formatDate(dates[1]) : formatDate(dates[0]);
      }

      const response = await fetchSales(params);
      if (!isMounted.current) return;

      if (response && response.status === 200) {
        const { data } = response.data;
        setDashboardData({
          totalSales: data || {},
          topSalesByLocation: data?.top_sales_by_location || [],
          topCategories: data?.top_categories || [],
          revenue: data?.revenue || {},
          recentOrders: data?.recent_orders || [],
          bestSellingProducts: data?.best_selling_products || [],
          bestSellingBrands: data?.best_selling_brands || []
        });
      } else {
        // Handle API errors gracefully
        const errorMessage = response?.message || 'Failed to load dashboard data. The API might be unavailable.';
        errorNotify(errorMessage);
        console.error('Dashboard API Error:', response);
        
        // Set fallback data to prevent undefined errors and provide some demo content
        setDashboardData({
          totalSales: {
            total_sell_amount: 0,
            total_orders: 0,
            growth_rate: 0
          },
          topSalesByLocation: [
            { city: "Demo City 1", percentage: "0" },
            { city: "Demo City 2", percentage: "0" },
            { city: "Demo City 3", percentage: "0" }
          ],
          topCategories: [],
          revenue: {
            data: []
          },
          recentOrders: [
            { id: "demo-001", customer: "Demo Customer", status: "Pending", amount: "$0.00" }
          ],
          bestSellingProducts: [
            { 
              id: "demo-001", 
              label: "Demo Product", 
              img: "placeholder.jpg", 
              price: 0,
              amount: 0,
              stock: 0,
              date: new Date().toLocaleDateString()
            }
          ],
          bestSellingBrands: []
        });
      }
    } catch (error) {
      if (isMounted.current) {
        errorNotify(error.message);
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  }, [formatDate, errorNotify]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const sidebarItems = [
    { to: "/dashboard", icon: "ri-bar-chart-line", text: "Sales" },
    { to: "/my-product-list", icon: "ri-product-hunt-line", text: "Product" },
    { to: "/oem-list", icon: "ri-building-2-line", text: "Company" },
    { to: "/user-list", icon: "ri-user-line", text: "User" },
    { to: "/employees", icon: "ri-group-line", text: "Employee" },
    { to: "/inventory-list", icon: "ri-archive-2-line", text: "Inventory" }
  ];

  const handleNavigation = useCallback((to) => {
    if (isMounted.current) {
      navigate(to);
    }
  }, [navigate]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Dashboard" pageTitle="Dashboards" />
          
          {/* Loading State */}
          {loading && (
            <Row>
              <Col>
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
                  <div className="text-center">
                    <Spinner color="primary" style={{ width: "3rem", height: "3rem" }} />
                    <p className="mt-3 text-muted">Loading dashboard data...</p>
                  </div>
                </div>
              </Col>
            </Row>
          )}
          
          {/* Dashboard Content - Only show when not loading */}
          {!loading && (
          <Row>
            <div className="mb-4 col-1">
              <ListGroup style={{ fontSize: '12px' }}>
                {sidebarItems.map((item, index) => (
                  <ListGroupItem 
                    key={index} 
                    onClick={() => handleNavigation(item.to)}
                    style={{ cursor: 'pointer' }}
                    className="d-flex align-items-center"
                  >
                    <i className={`${item.icon} me-2`}></i>
                    <span>{item.text}</span>
                  </ListGroupItem>
                ))}
              </ListGroup>
            </div>

            <Col md={rightColumn ? 9 : 11}>
              <div className="h-100">
                <Section 
                  rightClickBtn={toggleRightColumn} 
                  onDateChange={loadDashboardData}
                />
                <Row>
                  <Widget data={dashboardData.totalSales} />
                </Row>
                <Row>
                  <Col xl={8}>
                    <Revenue 
                      data={dashboardData.revenue} 
                      onChange={loadDashboardData}
                    />
                  </Col>
                  <SalesByLocations data={dashboardData.topSalesByLocation} />
                </Row>
                <Row>
                  <BestSellingProducts data={dashboardData.bestSellingProducts} />
                  <TopSellers />
                </Row>
                <Row>
                  <StoreVisits />
                  <RecentOrders data={dashboardData.recentOrders} />
                </Row>
              </div>
            </Col>

            <RecentActivity 
              rightColumn={rightColumn} 
              hideRightColumn={toggleRightColumn}
              data={dashboardData.recentOrders}
            />
          </Row>
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};

// Error Boundary Fallback Component
const DashboardErrorFallback = ({ error, resetErrorBoundary }) => (
  <div className="page-content">
    <Container fluid>
      <BreadCrumb title="Dashboard" pageTitle="Dashboards" />
      <Row>
        <Col>
          <Alert color="danger" className="text-center">
            <h4 className="alert-heading">Dashboard Loading Error</h4>
            <p>There was an issue loading the dashboard. This might be due to:</p>
            <ul className="text-left list-unstyled">
              <li>• API server is unavailable</li>
              <li>• Network connectivity issues</li>
              <li>• Backend services are down</li>
            </ul>
            <div className="mt-3">
              <button className="btn btn-outline-danger btn-sm me-2" onClick={resetErrorBoundary}>
                Try Again
              </button>
              <button className="btn btn-danger btn-sm" onClick={() => window.location.reload()}>
                Refresh Page
              </button>
            </div>
          </Alert>
        </Col>
      </Row>
    </Container>
  </div>
);

// Wrap the entire Dashboard component with ErrorBoundary
const SafeDashboard = () => (
  <ErrorBoundary
    FallbackComponent={DashboardErrorFallback}
    onError={(error, errorInfo) => {
      console.error('Dashboard Component Error:', error, errorInfo);
    }}
  >
    <DashboardEcommerce />
  </ErrorBoundary>
);

export default SafeDashboard;